import { EventHealthApiIssue } from '../../tools-reports/event-health/event-health.types';
import { HealthReviewFinding } from './event-health-form-review.types';
import { CERTIFICATE_TYPE_LABELS } from './event-health-certificate-check';
import { HEALTH_RULES, HealthRuleDefinition } from './event-health-rule-catalog';
import {
  asHealthField,
  compatibleHealthPath,
  HealthField,
  HealthGuidanceOptions,
  healthPathParts,
  healthRowIndex,
  normalizeHealthEvent,
  normalizeHealthScope,
  ownHealthValue,
} from './event-health-targets';

/**
 * User-facing help for stored event-health findings.
 *
 * Maintenance guide:
 * 1. Maintain code-to-field/copy entries in HEALTH_RULES (the Excel catalog).
 * 2. Preserve data-health-field markers when customizing a form. EVENT_CONTROLS
 *    remains a compatibility fallback for Angular inputs without markers.
 * 3. Keep structured certificate/master-data recovery here, shared by both views.
 *
 * Keep backend paths for diagnostics. Updated list operators return actual rows.
 * Certificate authorisation paths in older responses can still be placeholders.
 * This file describes guidance, not validation: changing a value is not proof
 * that the backend rule passes. Unknown rules retain their backend message.
 */
interface FieldHelp {
  label: string;
  help: string;
}

interface RuleHelp extends FieldHelp {
  selector?: string;
}

export interface EventHealthGuidance {
  target: HealthReviewFinding['target'];
  help: string;
  title?: string;
}

// These keys are semantic backend fields, without JSON-LD prefixes.
const FIELD_HELP: Record<string, FieldHelp> = {
  type: { label: 'Event details → Event type', help: 'The event type is supplied by the application. Ask support to check how this event is generated.' },
  bizStep: {
    label: 'Event details → Business step',
    help: 'The business step is supplied by the event workflow. Ask support to check how this event is generated.',
  },
  action: { label: 'Event details → Event action', help: 'The action is supplied by the event workflow. Ask support to check how this event is generated.' },
  eventTime: { label: 'Event details → Date', help: 'Check the event date and time.' },
  eventTimeZoneOffset: { label: 'Event details → Time zone', help: 'Select the time zone for the event date.' },
  lotNumber: { label: 'Products → Lot number', help: 'Enter the lot number for this event.' },
  aquacultureMethod: { label: 'Farm details → Farming method', help: 'Select the farming method.' },
  broodstockSource: { label: 'Hatching details → Broodstock source', help: 'Select Domestic or Wild Sources.' },
  proteinSource: { label: 'Feed details → Protein source', help: 'Provide the source of protein in the feed.' },
  feedType: { label: 'Feed details → Feed type', help: 'Use Natural, processed, live, GMO, or other.' },
  humanWelfarePolicy: { label: 'Compliance → Human welfare policy', help: 'Use None, Internal, or 3P Audit.' },
  catchArea: { label: 'Catch details → Catch area', help: 'Select the area where the catch was taken.' },
  vesselTripDate: { label: 'Catch details → Vessel trip date', help: 'Check the vessel trip date.' },
  fishingGearTypeCode: { label: 'Vessel details → Fishing gear', help: 'Select a fishing gear entry with a valid GDST code.' },
  productionMethod: { label: 'Production details → Production method', help: 'Check the production method required by this event type.' },
  productionMethodForFishAndSeafoodCode: { label: 'Production details → Production method', help: 'Check the production method required by this event type.' },
  countryOfOrigin: { label: 'Products → Country of origin', help: 'Provide the product country of origin.' },
  vesselName: { label: 'Vessel details → Vessel name', help: 'Check the selected vessel and its name.' },
  vesselID: { label: 'Vessel details → Vessel registration', help: 'Check the selected vessel and its registration identifier.' },
  vesselFlagState: { label: 'Vessel details → Flag state', help: 'Check the selected vessel and its flag state.' },
  parentID: { label: 'Parent container → SSCC', help: 'Select or enter a valid parent SSCC identifier.' },
  readPoint: { label: 'Location → Event location', help: 'Check the location where this event took place.' },
  bizLocation: { label: 'Location → Business location', help: 'Check the selected business location and its identifier.' },
  sourceList: { label: 'Locations and parties → Source', help: 'Check the source location or party described in the finding.' },
  destinationList: { label: 'Locations and parties → Destination', help: 'Check the destination location or party described in the finding.' },
  productOwner: {
    label: 'Event ownership → Product owner',
    help: 'Check the product owner identifier. This may come from event settings rather than a visible input.',
  },
  informationProvider: {
    label: 'Event ownership → Information provider',
    help: 'Check the information provider identifier. This may come from event settings rather than a visible input.',
  },
  bizTransactionList: { label: 'Documents → Business transactions', help: 'Check the document reference and transaction type.' },
  eventID: {
    label: 'Event details → Event identifier',
    help: 'The event identifier is generated by the application. If it is invalid, ask support to check the event record.',
  },
  disposition: {
    label: 'Event details → Disposition',
    help: 'This value is supplied by the event workflow. If it is invalid, ask support to check how the event is generated.',
  },
};

// Only list controls confirmed in the corresponding event template.
// A missing entry means we show guidance without guessing an input.
export const EVENT_CONTROLS: Record<string, Record<string, string>> = {
  fishing: {
    eventTime: 'eventTime',
    vesselTripDate: 'vesselTripDate',
    catchArea: 'catchArea',
    fishingGearTypeCode: 'fishingGearDescription',
    countryOfOrigin: 'countryOfOrigin',
    productionMethodForFishAndSeafoodCode: 'productionMethodForFishAndSeafoodCode',
    productionMethod: 'productionMethodForFishAndSeafoodCode',
    vesselName: 'vesselName',
    vesselID: 'vesselID',
    vesselFlagState: 'vesselFlagState',
    lotNumber: 'lot',
  },
  fishinglanding: { catchArea: 'catchArea', countryOfOrigin: 'countryOfOrigin', lotNumber: 'lot' },
  landing: { eventTime: 'landing_date' },
  transshipment: { eventTime: 'transshipmentDate' },
  farmharvest: { eventTime: 'harvest_date', aquacultureMethod: 'farming_method', lotNumber: 'lot' },
  hatching: {
    eventTime: 'eventTime',
    lotNumber: 'lotNumber',
    broodstockSource: 'broodstockSource',
    productOwner: 'productOwner',
    informationProvider: 'informationProvider',
  },
  feedmill: { eventTime: 'eventTime', lotNumber: 'lotNumber', productOwner: 'productOwner', informationProvider: 'informationProvider' },
  feeding: { eventTime: 'feeding_date' },
  processing: { eventTime: 'production_date', countryOfOrigin: 'countryOfOrigin' },
  onvesselprocessing: { eventTime: 'production_date', countryOfOrigin: 'countryOfOrigin' },
  commingling: { eventTime: 'eventTime' },
  aggregation: { eventTime: 'eventTime', parentID: 'parentID' },
  disaggregation: { eventTime: 'eventTime', parentID: 'parentID' },
  shipping: { eventTime: 'shipping_date' },
  receiving: { eventTime: 'receive_date' },
  transfer: { eventTime: 'shipping_date' },
  coldstorage: { eventTime: 'shipping_date' },
};

const RULE_HELP: Record<string, RuleHelp> = {
  'GDST12-WC-FISH-CERT-001': {
    label: 'Certifications → Fishing authorisation',
    help: 'Select a fishing authorisation with an issuing agency and a certificate number.',
    selector: 'app-certifications-display',
  },
  'GDST12-WC-LAND-CERT-001': {
    label: 'Certifications → Landing authorisation',
    help: 'Select a landing authorisation with an issuing agency and a certificate number.',
    selector: 'app-certifications-display',
  },
  'GDST12-WC-TRANS-CERT-001': {
    label: 'Certifications → Transshipment authorisation',
    help: 'Select a transshipment authorisation with an issuing agency and a certificate number.',
    selector: 'app-certifications-display',
  },
};

const PRODUCT_LIST_LABELS: Record<string, string> = {
  quantityList: 'Items',
  epcList: 'Items',
  inputQuantityList: 'Input items',
  inputEPCList: 'Input items',
  outputQuantityList: 'Output items',
  outputEPCList: 'Output items',
  childQuantityList: 'Child items',
  childEPCs: 'Child items',
};

/** Returns display text and a safe highlight target; never changes severity. */
export function mapEventHealthGuidance(eventType: string, issue: EventHealthApiIssue, options: HealthGuidanceOptions = {}): EventHealthGuidance {
  const event = normalizeHealthEvent(eventType);
  const definition: HealthRuleDefinition | undefined = Object.prototype.hasOwnProperty.call(HEALTH_RULES, issue.code)
    ? HEALTH_RULES[issue.code as keyof typeof HEALTH_RULES]
    : undefined;
  const applicable = !definition || definition.profiles.some((profile) => profile === '*' || normalizeHealthEvent(profile) === event);
  if (!applicable) {
    return {
      title: issue.title?.trim() || definition.title,
      help: issue.description?.trim() || definition.help,
      target: {
        controlName: '',
        label: 'Event details',
        path: issue.path,
        sectionOnly: true,
        unavailableReason: 'This rule does not identify a field in this event form.',
      },
    };
  }

  const structured = !!issue.reason || !!issue.requiredCertificateType;
  const certificateRecovery = !!ownHealthValue(RULE_HELP, issue.code) || issue.code.startsWith('AT-GDST-CERTIFICATE-');
  const compatible = !!definition && compatibleHealthPath(definition.path, issue.path);
  // Code selects the field. A compatible runtime path only refines its occurrence.
  // Never use the spreadsheet's /0/ examples as an actual item address.
  const catalogParts = healthPathParts(definition?.path);
  const exampleIndex = catalogParts.findIndex((part) => healthRowIndex(part) !== undefined);
  const fallbackPath = definition && (exampleIndex < 0 ? definition.path : '/' + catalogParts.slice(0, exampleIndex).join('/'));
  const effectiveIssue: EventHealthApiIssue = {
    ...issue,
    category: normalizeHealthScope(issue.category),
    field: definition?.field ?? issue.field,
    path: definition && !structured ? (compatible ? issue.path : fallbackPath) : issue.path,
  };
  const mapped = buildEventHealthGuidance(eventType, effectiveIssue);
  const semantic = asHealthField(definition?.field ?? semanticField(effectiveIssue, healthPathParts(effectiveIssue.path)));
  const masterData = effectiveIssue.category === 'master-data' || issue.reason?.startsWith('master-data-');
  const override = { ...(semantic ? ownHealthValue(options.fields, semantic) : {}), ...ownHealthValue(options.rules, issue.code) };
  const parts = healthPathParts(effectiveIssue.path);
  const productList = !!ownHealthValue(PRODUCT_LIST_LABELS, parts[0]);
  const collection = productList ? (parts[0].startsWith('input') ? 'input' : parts[0].startsWith('output') ? 'output' : 'items') : undefined;
  const field = masterData ? undefined : (asHealthField(override.field) ?? semantic);
  const missingLocation = !!definition && !!issue.path && !compatible && !structured;
  const sectionOnly = masterData || override.sectionOnly || !field;

  const result: EventHealthGuidance = {
    ...mapped,
    // Structured reasons can change the recovery action (e.g. unavailable master data).
    title: override.title?.trim() || (structured ? mapped.title || issue.title : definition?.title) || mapped.title,
    help:
      override.help?.trim() ||
      (structured || certificateRecovery ? mapped.help : definition?.help) ||
      mapped.help ||
      issue.description?.trim() ||
      'Review this event and save any corrections.',
    target: {
      ...mapped.target,
      path: issue.path,
      field,
      collection: override.collection ?? collection,
      // EPC identifier arrays may differ from quantity-list rows; keep them at section level.
      rowIndex:
        productList && (parts[0].endsWith('QuantityList') || parts[0] === 'quantityList')
          ? !definition || compatible
            ? healthRowIndex(parts[1])
            : undefined
          : undefined,
      sectionOnly,
      // Changing a semantic field must not retain a fallback to the old Angular control.
      controlName: masterData || (override.field && override.field !== semantic) ? '' : mapped.target.controlName,
      unavailableReason: missingLocation ? 'The item location could not be matched. Review the indicated section.' : undefined,
    },
  };
  if (issue.code !== 'EPCIS20-SCHEMA-REQUIRED') return result;

  // The updated schema validator provides a concrete missing-property path.
  // Parent-only errors from alternative schema branches remain general findings.
  const lastPart = issue.path?.split('/').filter(Boolean).at(-1);
  const missingField = lastPart?.replace(/~1/g, '/').replace(/~0/g, '~');
  if (!missingField || /^\d+$/.test(missingField) || mapped.target.label === 'Event details') return result;
  const label = mapped.target.label.split(' → ').at(-1) || 'Required information';
  return { ...result, title: override.title?.trim() || `${label} is missing` };
}

/** All catalog codes for an HTML field, including aliases; no second hand-maintained list. */
export function healthRulesForField(field: HealthField, eventType?: string): string[] {
  return Object.entries(HEALTH_RULES)
    .filter(
      ([, definition]) =>
        asHealthField(definition.field) === field &&
        (!eventType || definition.profiles.some((profile) => profile === '*' || normalizeHealthEvent(profile) === normalizeHealthEvent(eventType))),
    )
    .map(([code]) => code);
}

function buildEventHealthGuidance(eventType: string, issue: EventHealthApiIssue): EventHealthGuidance {
  const event = normalizeHealthEvent(eventType);
  const segments = (issue.path ?? '')
    .split('/')
    .filter(Boolean)
    .map((part) => part.replace(/~1/g, '/').replace(/~0/g, '~'));
  const field = semanticField(issue, segments);
  // Structured reasons take priority over generic rule wording.
  if (issue.reason?.startsWith('master-data-')) {
    return masterDataGuidance(issue, field);
  }
  if (issue.requiredCertificateType || segments.some((part) => part.includes('certificationList')) || issue.code.startsWith('AT-GDST-CERTIFICATE-')) {
    return certificateGuidance(issue);
  }
  const ruleHelp = ownHealthValue(RULE_HELP, issue.code);
  if (ruleHelp) return guidance(issue, ruleHelp.label, ruleHelp.help, '', ruleHelp.selector);

  const listLabel = ownHealthValue(PRODUCT_LIST_LABELS, segments[0]);
  if (listLabel) {
    const exactRow = /^\d+$/.test(segments[1] ?? '');
    const rowLabel = exactRow ? ` → Item ${Number(segments[1]) + 1}` : '';
    let detail = '';
    let help = 'Review the items in this section. If the list is empty, add an item.';
    if (field === 'quantity') {
      detail = ' → Quantity';
      help = exactRow ? 'Enter a quantity greater than zero for this item.' : 'Add an item with a quantity greater than zero.';
    } else if (field === 'uom') {
      detail = ' → Unit';
      help = exactRow ? 'Open the indicated item and select an accepted unit of measure.' : 'Review the item units and select an accepted unit of measure.';
    } else if (field === 'epcClass') {
      detail = ' → Product / lot';
      help = 'Open the item editor and check the selected product and lot identifier.';
    }
    if (exactRow && segments.length === 2) {
      detail = ' → Identifier';
      help = 'Open this item and check its product identifier.';
    }
    if (segments.length === 1) {
      detail = '';
      help = 'Add an item to this list.';
    }
    const group = segments[0].startsWith('input') ? 'input' : segments[0].startsWith('output') ? 'output' : 'items';
    const hasSeparateTables = ['processing', 'onvesselprocessing', 'commingling', 'feedmill'].includes(event);
    const selector = hasSeparateTables && group !== 'items' ? `[data-health-products="${group}"]` : 'app-product-lines-table';
    return guidance(issue, listLabel + rowLabel + detail, help, '', selector);
  }

  const fieldHelp = ownHealthValue(FIELD_HELP, field);
  if (['sourceList', 'destinationList', 'bizTransactionList'].includes(segments[0]) && /^\d+$/.test(segments[1] ?? '')) {
    const fieldLabel = segments.at(-1) === 'type' ? 'Type' : 'Identifier';
    return guidance(issue, `${FIELD_HELP[segments[0]].label} → Entry ${Number(segments[1]) + 1} → ${fieldLabel}`, issue.description);
  }
  if (!fieldHelp) {
    // Temperature warnings include a precise report and missing-field path.
    if (segments[0] === 'sensorElementList') {
      const report = segments.indexOf('sensorReport');
      const elementNumber = numericLabel(segments[1], 'Sensor');
      const reportNumber = numericLabel(segments[report + 1], 'Report');
      const missingField = segments.at(-1) === 'uom' ? 'Unit' : segments.at(-1) === 'value' ? 'Value' : issue.label;
      return guidance(issue, ['Temperature reports', elementNumber, reportNumber, missingField].filter(Boolean).join(' → '), issue.description);
    }
    // Root schema errors cannot reliably name an input. Do not display "event"
    // or invent a field by extracting words from the exception message.
    const label = issue.path === '/' ? 'Event details' : issue.label || 'Event details';
    return guidance(issue, label, '');
  }

  if (field === 'readPoint' && ['fishing', 'transshipment'].includes(event)) {
    return guidance(issue, fieldHelp.label, fieldHelp.help, '', '[formGroupName="readPoint"]');
  }

  let control = ownHealthValue(ownHealthValue(EVENT_CONTROLS, event), field) ?? '';
  if (field === 'eventTimeZoneOffset' && EVENT_CONTROLS[event] && event !== 'feeding') control = 'timezoneId';
  if (
    field === 'humanWelfarePolicy' &&
    [
      'farmharvest',
      'hatching',
      'feedmill',
      'processing',
      'onvesselprocessing',
      'commingling',
      'aggregation',
      'disaggregation',
      'shipping',
      'receiving',
      'transfer',
      'coldstorage',
      'landing',
    ].includes(event)
  )
    control = 'humanWelfarePolicy';

  // Master-data failures must not look resolved just because a local field changes.
  if (issue.category === 'master-data') control = '';
  const help =
    issue.category === 'master-data' || issue.category === 'event-or-master-data'
      ? `${fieldHelp.help} This requirement may need a correction to the linked master-data record and a recheck.`
      : fieldHelp.help;
  return guidance(issue, fieldHelp.label, help, control);
}

function semanticField(issue: EventHealthApiIssue, segments: string[]): string {
  // Parent context matters: readPoint/id and bizLocation/id are different inputs.
  if (['readPoint', 'bizLocation', 'sourceList', 'destinationList', 'bizTransactionList'].includes(segments[0])) return segments[0];
  const raw = issue.field && !issue.field.includes('/') ? issue.field : (segments.at(-1) ?? '');
  const field = raw.replace(/^[^:]+:/, '');
  const pathField = (segments.at(-1) ?? '').replace(/^[^:]+:/, '');
  // Some rules use a broader field alias (for example fishingGear). The path
  // still identifies the known input, so do not discard that information.
  return ownHealthValue(FIELD_HELP, field) ? field : ownHealthValue(FIELD_HELP, pathField) ? pathField : field;
}

function guidance(issue: EventHealthApiIssue, label: string, help: string, controlName = '', selector?: string): EventHealthGuidance {
  return { target: { label, controlName, path: issue.path, selector }, help };
}

const CERTIFICATE_FIELDS: Record<string, string> = {
  'gdst:certificationType': 'Certificate type',
  certificationType: 'Certificate type',
  certificationAgency: 'Issuing agency',
  certificationIdentification: 'Certificate number',
  certificationStandard: 'Certification standard',
};

function numericLabel(value: string | undefined, label: string): string {
  return /^\d+$/.test(value ?? '') ? `${label} ${Number(value) + 1}` : '';
}

function masterDataGuidance(issue: EventHealthApiIssue, field: string): EventHealthGuidance {
  const recordNames: Record<string, string> = {
    VESSEL: 'Vessel',
    LOCATION: 'Location',
    TRADE_ITEM: 'Product',
    PARTY: 'Trading party',
  };
  const record = recordNames[issue.entityType ?? ''] || 'Linked';
  const fieldLabel = FIELD_HELP[field]?.label.split(' → ').at(-1) || issue.label || 'Required information';
  let title = `${fieldLabel} needs attention in the linked record`;
  let help = `Check ${fieldLabel.toLowerCase()} in the linked ${record.toLowerCase()} record, then run event validation again.`;
  if (issue.reason === 'master-data-not-found') {
    title = `${record} record was not found`;
    help = `Check that the selected ${record.toLowerCase()} exists and its identifier is correct.`;
  } else if (issue.reason === 'master-data-unavailable') {
    title = `${record} record could not be checked`;
    help = 'Try checking the event again when the linked record is available. No data correction has been confirmed.';
  } else if (issue.reason !== 'master-data-field-missing') {
    // Preserve future backend reasons rather than pretending they mean missing data.
    return guidance(issue, `${record} record → ${fieldLabel}`, issue.description);
  }
  // Never bind a master-data field to a same-named local input. Editing the
  // event cannot prove that its referenced record was corrected.
  return { ...guidance(issue, `${record} record → ${fieldLabel}`, help), title };
}

function certificateLocation(path: string): string {
  const parts = path.split('/').filter(Boolean);
  const row = parts.indexOf('certification');
  const number = row >= 0 ? numericLabel(parts[row + 1], 'Certificate') : '';
  const field = CERTIFICATE_FIELDS[parts.at(-1) ?? ''];
  const list = parts[0] === 'ilmd' ? 'Product certifications' : 'Certifications';
  return [list, number, field].filter(Boolean).join(' → ');
}

function certificateGuidance(issue: EventHealthApiIssue): EventHealthGuidance {
  const ruleHelp = RULE_HELP[issue.code];
  const typeLabel = CERTIFICATE_TYPE_LABELS[issue.requiredCertificateType ?? ''];
  const label = typeLabel ? `Certifications → ${typeLabel}` : ruleHelp?.label || issue.label || 'Certifications';
  const selector = 'app-certifications-display';

  if (issue.reason === 'missing-matching-entry') {
    return guidance(
      issue,
      label,
      `Add a ${typeLabel?.toLowerCase() || 'certificate of the required type'} with an issuing agency and certificate number.`,
      '',
      selector,
    );
  }

  if (issue.reason === 'incomplete-entry') {
    // One authorisation finding can describe several incomplete alternatives.
    // Keep each missing field with its own certificate, including its list.
    const missing = [...new Set((issue.missingPaths ?? []).map(certificateLocation))];
    const help = missing.length
      ? `Complete a matching certificate. Missing: ${missing.join('; ')}.`
      : 'Complete the required information on a matching certificate.';
    return guidance(issue, label, help, '', selector);
  }

  if (issue.code === 'AT-GDST-CERTIFICATE-TYPE-001') {
    return guidance(
      issue,
      certificateLocation(issue.path ?? ''),
      'Select a supported certificate type in certificate master data. Legacy names and misspellings are not accepted.',
      '',
      selector,
    );
  }

  if (issue.reason === 'missing-required-value') {
    // The backend repeats the rule's full missingPaths on each finding. Use the
    // finding's own path here so a warning for Certificate 2 stays about row 2.
    const path = issue.path || issue.missingPaths?.[0] || '';
    const field = CERTIFICATE_FIELDS[path.split('/').at(-1) ?? ''];
    return guidance(
      issue,
      certificateLocation(path),
      field
        ? `Complete the ${field.toLowerCase()} in certificate master data, then reselect the certificate.`
        : 'Complete the missing certificate information in certificate master data.',
      '',
      selector,
    );
  }

  // Older stored authorisation findings do not have structured reasons.
  if (ruleHelp) return guidance(issue, ruleHelp.label, ruleHelp.help, '', selector);
  return guidance(issue, issue.label || certificateLocation(issue.path ?? ''), issue.description, '', selector);
}
